import { FormEvent, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { CalendarClock, CheckCircle2, ClipboardList, Copy, RefreshCw, Search, TimerReset } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "../admin/components/Pagination";
import StoreLayout from "./StoreLayout";
import { hasStorePermission } from "./storePermissions";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import {
  StoreFollowUp,
  StorePaginationMeta,
  StoreUser,
  completeStoreFollowUp,
  getCurrentStoreUser,
  listStoreFollowUps,
  logoutStore,
  postponeStoreFollowUp,
} from "./api";
import { copyPhoneNumber } from "./phoneActions";

const defaultPagination: StorePaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

function currentYearStart() {
  return `${new Date().getFullYear()}-01-01`;
}

function currentYearEnd() {
  return `${new Date().getFullYear()}-12-31`;
}

export default function StoreFollowUps() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [items, setItems] = useState<StoreFollowUp[]>([]);
  const [selectedItem, setSelectedItem] = useState<StoreFollowUp | null>(null);
  const [counts, setCounts] = useState({ Total: 0, Overdue: 0, Today: 0, Upcoming: 0, Completed: 0 });
  const [pagination, setPagination] = useState<StorePaginationMeta>(defaultPagination);
  const [statusOptions, setStatusOptions] = useState<{ Key: string; Label: string }[]>([]);
  const [status, setStatus] = useState(searchParams.get("status") || "Open");
  const [keyword, setKeyword] = useState(searchParams.get("keyword") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("DateFrom") || currentYearStart());
  const [dateTo, setDateTo] = useState(searchParams.get("DateTo") || currentYearEnd());
  const [completeNote, setCompleteNote] = useState("");
  const [postponeAt, setPostponeAt] = useState("");
  const [postponeNote, setPostponeNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const effectiveLocationId = useMemo(() => {
    return preferredStoreLocationId(user, currentLocationId);
  }, [currentLocationId, user]);
  const canHandleFollowUps = hasStorePermission(user, "store-follow-ups-write");

  async function load(filters = { LocationId: effectiveLocationId, Status: status, Keyword: keyword, DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage }) {
    if (!filters.LocationId) return;
    setLoading(true);
    setMessage("");
    try {
      const data = await listStoreFollowUps(filters);
      setItems(data.FollowUps || []);
      setCounts(data.Counts || { Total: 0, Overdue: 0, Today: 0, Upcoming: 0, Completed: 0 });
      setPagination(data.Pagination || defaultPagination);
      setStatusOptions(data.StatusOptions || []);
      setSelectedItem((current) => {
        if (!current) return data.FollowUps?.[0] || null;
        return data.FollowUps?.find((item) => item.FollowUpId === current.FollowUpId) || data.FollowUps?.[0] || null;
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "回訪待辦載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await getCurrentStoreUser();
        if (!alive) return;
        setUser(me.User);
        setCurrentLocationId(preferredStoreLocationId(me.User, currentLocationId));
      } catch {
        navigate("/store-login", { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [currentLocationId, navigate]);

  useEffect(() => {
    if (!effectiveLocationId) return;
    load({ LocationId: effectiveLocationId, Status: status, Keyword: keyword.trim(), DateFrom: dateFrom, DateTo: dateTo, Page: 1, PerPage: defaultPagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocationId]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (status && status !== "Open") next.set("status", status);
    if (keyword.trim()) next.set("keyword", keyword.trim());
    if (dateFrom) next.set("DateFrom", dateFrom);
    if (dateTo) next.set("DateTo", dateTo);
    setSearchParams(next, { replace: true });
  }, [dateFrom, dateTo, keyword, setSearchParams, status]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    setSelectedItem(null);
    setCompleteNote("");
    setPostponeAt("");
    setPostponeNote("");
    load({ LocationId: locationId, Status: status, Keyword: keyword.trim(), DateFrom: dateFrom, DateTo: dateTo, Page: 1, PerPage: pagination.PerPage });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ LocationId: effectiveLocationId, Status: status, Keyword: keyword.trim(), DateFrom: dateFrom, DateTo: dateTo, Page: 1, PerPage: pagination.PerPage });
  }

  async function handleComplete() {
    if (!selectedItem) return;
    if (!canHandleFollowUps) {
      setMessage("此帳號沒有完成回訪權限。");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await completeStoreFollowUp(selectedItem.FollowUpId, { Note: completeNote.trim() });
      setCompleteNote("");
      await load({ LocationId: effectiveLocationId, Status: status, Keyword: keyword.trim(), DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "完成回訪失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handlePostpone() {
    if (!selectedItem) return;
    if (!canHandleFollowUps) {
      setMessage("此帳號沒有延期回訪權限。");
      return;
    }
    if (!postponeAt) {
      setMessage("請選擇新的回訪時間。");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await postponeStoreFollowUp(selectedItem.FollowUpId, { NextFollowUpAt: postponeAt, Note: postponeNote.trim() });
      setPostponeAt("");
      setPostponeNote("");
      await load({ LocationId: effectiveLocationId, Status: status, Keyword: keyword.trim(), DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "延期回訪失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="回訪待辦" user={user}>
      <form className="mb-4 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 sm:grid-cols-2 2xl:grid-cols-[minmax(180px,220px)_minmax(300px,1fr)_minmax(180px,200px)_minmax(180px,200px)_auto] 2xl:items-end 2xl:p-4" onSubmit={handleSearch}>
        <Field label="回訪狀態">
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            {statusOptions.length === 0 && <option value="Open">待處理</option>}
            {statusOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
          </select>
        </Field>
        <Field label="關鍵字">
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="客戶姓名、電話、回訪內容" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </Field>
        <Field label="起日">
          <input className="h-11 w-full min-w-[180px] rounded-md border border-[#cfd8d2] px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
        </Field>
        <Field label="迄日">
          <input className="h-11 w-full min-w-[180px] rounded-md border border-[#cfd8d2] px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
        </Field>
        <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => load()} type="button"><RefreshCw size={18} />整理</button>
        </div>
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <Summary label="待處理" value={counts.Total || 0} />
        <Summary label="逾期" tone="danger" value={counts.Overdue || 0} />
        <Summary label="今日" tone="warning" value={counts.Today || 0} />
        <Summary label="未來" value={counts.Upcoming || 0} />
        <Summary label="已完成" value={counts.Completed || 0} />
      </div>

      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(360px,.72fr)]">
        <section className="rounded-md border border-[#dfe6e1] bg-white">
          <div className="grid gap-3 p-3">
            {loading ? (
              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-8 text-center text-[#59665f]">載入中...</div>
            ) : items.length === 0 ? (
              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-8 text-center text-[#59665f]">目前沒有符合條件的回訪。</div>
            ) : items.map((item) => (
              <button
                className={`grid gap-3 rounded-md border p-4 text-left transition hover:bg-[#f7faf8] ${selectedItem?.FollowUpId === item.FollowUpId ? "border-[#9ac8aa] bg-[#f1f8f3]" : "border-[#edf0ee] bg-white"}`}
                key={item.FollowUpId}
                onClick={() => setSelectedItem(item)}
                type="button"
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-2">
                      <Badge tone={item.IsOverdue ? "danger" : item.IsToday ? "warning" : item.Status === "Completed" ? "muted" : "green"}>{item.StatusLabel}</Badge>
                      {item.SourceType && <Badge>{sourceLabel(item.SourceType)}</Badge>}
                    </div>
                    <h2 className="mt-3 break-words text-lg font-extrabold text-[#203d32]">{item.CustomerName}</h2>
                    <p className="mt-1 break-all text-sm font-bold text-[#59665f]">{item.CustomerPhone}</p>
                  </div>
                  <CalendarClock className="shrink-0 text-[#315746]" size={22} />
                </div>
                <p className="line-clamp-2 whitespace-pre-line text-sm leading-6 text-[#59665f]">{item.Subject}</p>
                <div className="text-xs font-bold text-[#7b877f]">回訪時間：{item.NextFollowUpAt || "-"}</div>
              </button>
            ))}
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ LocationId: effectiveLocationId, Status: status, Keyword: keyword.trim(), DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: pagination.PerPage })} />
        </section>

        <aside className="rounded-md border border-[#dfe6e1] bg-white">
          {!selectedItem ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center text-[#59665f]">
              <CalendarClock className="mb-3 text-[#9aa8a0]" size={34} />
              <p className="font-bold text-[#203d32]">選擇一筆回訪</p>
              <p className="mt-2 text-sm leading-6">這裡會顯示客戶、來源、回訪內容，並可完成或延期。</p>
            </div>
          ) : (
            <div className="grid gap-4 p-4">
              <div className="border-b border-[#edf0ee] pb-4">
                <div className="flex flex-wrap gap-2">
                  <Badge tone={selectedItem.IsOverdue ? "danger" : selectedItem.IsToday ? "warning" : selectedItem.Status === "Completed" ? "muted" : "green"}>{selectedItem.StatusLabel}</Badge>
                  {selectedItem.SourceType && <Badge>{sourceLabel(selectedItem.SourceType)}</Badge>}
                </div>
                <h2 className="mt-3 break-words text-2xl font-extrabold text-[#203d32]">{selectedItem.CustomerName}</h2>
                <p className="mt-1 break-all text-base font-bold text-[#59665f]">{selectedItem.CustomerPhone}</p>
                <div className="mt-3 text-sm font-bold text-[#7b877f]">回訪時間：{selectedItem.NextFollowUpAt || "-"}</div>
              </div>

              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-4">
                <p className="text-sm font-bold text-[#7b877f]">回訪內容</p>
                <p className="mt-2 whitespace-pre-wrap break-words leading-7 text-[#34463c]">{selectedItem.Subject || "待回訪"}</p>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => copyPhoneNumber(selectedItem.CustomerPhone)} type="button">
                  <Copy size={18} />複製電話
                </button>
                {selectedItem.DetailPath ? (
                  <Link className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" to={selectedItem.DetailPath}>
                    <ClipboardList size={18} />來源資料
                  </Link>
                ) : (
                  <Link className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" to={`/store/customers/${encodeURIComponent(selectedItem.CustomerPhone)}`}>
                    <ClipboardList size={18} />客戶資料
                  </Link>
                )}
              </div>

              {selectedItem.Status !== "Completed" ? (
                <>
                  {canHandleFollowUps ? (
                    <>
                      <section className="grid gap-3 rounded-md border border-[#dfe6e1] p-4">
                        <div className="flex items-center gap-2 text-base font-extrabold text-[#203d32]"><CheckCircle2 size={18} />完成回訪</div>
                        <textarea className="min-h-24 rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如：已通知保養時間，客戶下週到店。" value={completeNote} onChange={(event) => setCompleteNote(event.target.value)} />
                        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={saving} onClick={handleComplete} type="button">
                          <CheckCircle2 size={18} />{saving ? "處理中" : "標記完成"}
                        </button>
                      </section>

                      <section className="grid gap-3 rounded-md border border-[#dfe6e1] p-4">
                        <div className="flex items-center gap-2 text-base font-extrabold text-[#203d32]"><TimerReset size={18} />延期回訪</div>
                        <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="datetime-local" value={postponeAt} onChange={(event) => setPostponeAt(event.target.value)} />
                        <textarea className="min-h-20 rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="延期原因或下次重點" value={postponeNote} onChange={(event) => setPostponeNote(event.target.value)} />
                        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] hover:bg-[#f0f5f2] disabled:opacity-60" disabled={saving} onClick={handlePostpone} type="button">
                          <TimerReset size={18} />{saving ? "處理中" : "儲存延期"}
                        </button>
                      </section>
                    </>
                  ) : (
                    <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4 text-sm font-bold text-[#7b877f]">此帳號可查看回訪內容，但沒有完成或延期權限。</div>
                  )}
                </>
              ) : (
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4 text-sm leading-7 text-[#59665f]">
                  <p className="font-bold text-[#203d32]">完成時間：{selectedItem.CompletedAt || "-"}</p>
                  {selectedItem.CompletedNote && <p className="mt-2 whitespace-pre-wrap break-words">{selectedItem.CompletedNote}</p>}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </StoreLayout>
  );
}

function Field({ children, label }: { children: ReactNode; label: string }) {
  return <label className="grid gap-1 text-sm text-[#59665f]"><span className="font-bold">{label}</span>{children}</label>;
}

function Summary({ label, tone = "default", value }: { label: string; tone?: "default" | "warning" | "danger"; value: number }) {
  const className = tone === "danger" ? "border-[#f2c9c6] bg-[#fdf3f2]" : tone === "warning" ? "border-[#f0ddb0] bg-[#fff9ed]" : "border-[#dfe6e1] bg-white";
  return <div className={`rounded-md border px-4 py-3 ${className}`}><p className="text-xs font-bold text-[#7b877f]">{label}</p><p className="mt-1 text-2xl font-bold text-[#203d32]">{value}</p></div>;
}

function Badge({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "green" | "warning" | "danger" }) {
  const className =
    tone === "green" ? "bg-[#e9f4ec] text-[#245438]"
    : tone === "warning" ? "bg-[#fff7e8] text-[#9a5b16]"
    : tone === "danger" ? "bg-[#fdebea] text-[#9a2d2d]"
    : "bg-[#eef3f8] text-[#255071]";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${className}`}>{children}</span>;
}

function sourceLabel(sourceType: string) {
  const labels: Record<string, string> = {
    Customer: "客戶互動",
    FathersDay: "預約",
    ProductTrial: "試戴預約",
    ServiceRecord: "服務紀錄",
    StoreSalesOrder: "門市訂單",
  };
  return labels[sourceType] || sourceType;
}
